import { Response } from 'express'
import { IPage } from 'marketing-site/@types/generated/contentful'
import { getAssignedVariations } from 'marketing-site/lib/abTests'
import getLocaleCodeAndPathFromUri from 'marketing-site/lib/getLocaleAndPathFromUri'
import getPageByPath from 'marketing-site/lib/getPageByPath'
import { IWebinar, Webinar } from 'marketing-site/src/components/bespoke/webinars'
import EntryMarker from 'marketing-site/src/components/common/EntryMarker'
import Page from 'marketing-site/src/components/common/Page'
import { IAssignedVariations } from 'marketing-site/src/components/context/AssignedVariationsContext'
import { GetServerSideProps } from 'next'
import React from 'react'
import { getServerSideProps as getErrorServerSideProps } from '../../_error'

interface IProps {
  page: IPage
  webinar?: IWebinar
  relatedWebinars?: IWebinar[]
  assignedVariations: IAssignedVariations
}

export default function WebinarPage({
  webinar,
  page,
  relatedWebinars,
  nonce,
}: IProps & { nonce: string }) {
  if (!webinar || !relatedWebinars) {
    return (
      <EntryMarker entry={page}>
        <Page page={page} />
      </EntryMarker>
    )
  }

  const seo = {
    metaTitle: webinar.title,
    metaDescription: webinar.subtitle,
    openGraphTitle: webinar.title,
    openGraphImage: webinar.shareImage,
    openGraphDescription: webinar.subtitle,
    twitterImage: webinar.shareImage,
    disableCrawling: false,
    metaCanonical: `https://www.intercom.com${webinar.url}`,
  }

  return (
    <EntryMarker entry={page}>
      <Page page={page} title={webinar.title} seo={seo}>
        <Webinar {...webinar} relatedWebinars={relatedWebinars} />
        <script src="//fast.wistia.net/assets/external/E-v1.js" nonce={nonce} async></script>
      </Page>
    </EntryMarker>
  )
}

export const getServerSideProps: GetServerSideProps<IProps> = async function ({
  query,
  req,
  res,
  resolvedUrl,
}) {
  const page = await getPageByPath('webinar')
  if (!page) {
    res.statusCode = 404
    return getErrorServerSideProps({ req, res, query: {}, resolvedUrl })
  }
  const assignedVariations = await getAssignedVariations(req, page)

  const { getWebinar, getAllWebinars } = await import('marketing-site/lib/webinar')
  const { transformWebinar } = await import('marketing-site/src/transformers/Webinar')
  const { transformAndFilterWebinars } = await import('marketing-site/src/transformers/Webinars')

  const { locale } = await getLocaleCodeAndPathFromUri(resolvedUrl.split('/') ?? [])
  const webinarData = await getWebinar((query.webinarSlug as string).toLowerCase(), locale)

  if (!webinarData) {
    ;(res as Response).redirect('/webinars')
    // we need early return here to prevent any errors downstream
    return getErrorServerSideProps({ req, res, query: {}, resolvedUrl })
  }

  const webinar = await transformWebinar(webinarData)
  const allWebinars = await getAllWebinars()
  const enrichedWebinars = await transformAndFilterWebinars(allWebinars)
  const relatedWebinars = enrichedWebinars
    .filter((w) => w.isScheduled == webinar.isScheduled && w.slug != webinar.slug)
    .sort(() => 0.5 - Math.random())
    .slice(0, 3)

  return { props: { page, assignedVariations, webinar, relatedWebinars } }
}
